import InfoIcon from '@mui/icons-material/Info'
import Alert from '@mui/material/Alert'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useQueries } from '@tanstack/react-query'
import KNCaption from 'components/KNCaption/KNCaption'
import { KNTrailerIcon, KNTruckIcon } from 'components/KNIcon/KNIcon'
import { KNTemperatureTrackingIcon } from 'components/KNIcon/KNMaterialIcon'
import KNLoader from 'components/KNLoader/KNLoader'
import KNQuoteAttribute from 'components/KNQuoteAttribute/KNQuoteAttribute'
import { GuestUserContext } from 'context/authentication/GuestUserContext'
import { isPast } from 'date-fns'
import { analyticsEvent, analyticsPageView } from 'global/helpers/analytics'
import { localDate } from 'global/helpers/dates'
import { getFormattedUnit, getTemperatureMeasurement, getTemperatureUnit } from 'global/helpers/units'
import Footer from 'modules/Footer/Footer'
import TopNavigation from 'modules/TopNavigation/TopNavigation'
import { type ReactElement, useContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import CarrierQuoteAnswerSummary from 'screens/QuoteDetails/CarrierQuoteAnswerSummary'
import CarrierQuoteForm from 'screens/QuoteDetails/CarrierQuoteForm'
import QuoteAlert from 'screens/QuoteDetails/QuoteAlert'
import { isQuoteAnswerNotFoundError, isQuoteExpiredError } from 'screens/QuoteDetails/QuoteDetails.helpers'
import { isQuoteOfferAnswer } from 'screens/QuoteDetails/QuoteDetails.service'
import { type QuoteAnswer, QuoteStatus } from 'screens/QuoteDetails/QuoteDetails.types'
import QuoteError from 'screens/QuoteDetails/QuoteError'
import QuoteShipmentAccordion from 'screens/QuoteDetails/QuoteShipmentAccordion'
import { quoteAnswerQueryOptions, quoteRequestQueryOptions } from 'screens/Quotes/Quotes.service'

const QuoteLink = (): ReactElement => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { t } = useTranslation()
  const { token } = useParams<{
    token: string
  }>()
  const [guestUserState, _guestUserDispatch] = useContext(GuestUserContext)
  const [quoteRequestQuery, quoteAnswerQuery] = useQueries({
    queries: [quoteRequestQueryOptions(token), quoteAnswerQueryOptions(token)],
  })
  const [quoteStatus, setQuoteStatus] = useState<QuoteStatus>(QuoteStatus.OPEN)
  const [quoteAlert, setQuoteAlert] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    analyticsPageView('polestar/cs/quotelink')
  }, [])

  const handleQuoteAnswer = (answer: QuoteAnswer): void => {
    setQuoteStatus(answer.status)
    setSubmitted(true)
    analyticsEvent(
      isQuoteOfferAnswer(answer)
        ? 'polestar_carrier_quotes_quote_accepted'
        : 'polestar_carrier_quotes_quote_rejected',
    )
    setQuoteAlert(true)
  }

  const handleSnackbarClose = (): void => {
    setQuoteAlert(false)
  }

  const stackStyle = { margin: isMobile ? 2 : 4 }

  const hasTemperatureRequirements = quoteRequestQuery.data?.requirements?.temperature?.minimum !== null
    && quoteRequestQuery.data?.requirements?.temperature?.maximum !== null
  const hasTrailerRequirements = quoteRequestQuery.data?.requirements?.trailer?.details !== ''
  const hasTruckRequirements = quoteRequestQuery.data?.requirements?.truck?.details !== ''
  const isExpired = !!quoteRequestQuery.data && isPast(new Date(quoteRequestQuery.data.expirationDate))
  const isEditable = !!quoteAnswerQuery.data && quoteAnswerQuery.data.editable === true

  return (
    <Container
      maxWidth="xl"
      sx={{
        paddingTop: 2,
        paddingBottom: 1,
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: '100vh',
      }}
    >
      <Stack spacing={2} sx={{ paddingTop: 4, flexGrow: 1 }}>
        <TopNavigation />
        {quoteRequestQuery.isPending && (
          <KNLoader>
            <Typography>{t('screens.cs.carrier_quotes.loading')}</Typography>
          </KNLoader>
        )}
        {quoteRequestQuery.isError && (
          isQuoteExpiredError(quoteRequestQuery.error)
            ? <QuoteError quoteLinkError="expired" />
            : <QuoteError quoteLinkError="not-found" />
        )}
        {quoteRequestQuery.isSuccess && (
            <Stack
              direction={{ xs: 'column' }}
              spacing={{ xs: 2 }}
              alignItems="stretch"
              sx={{
                position: 'relative',
                flexGrow: 1,
              }}
            >
              {isExpired && (
                <Alert severity="warning" elevation={4}>
                  <Trans i18nKey="screens.cs.carrier_quotes.quote_has_expired" />
                </Alert>
              )}
              {isEditable && !submitted && (
                <Alert severity="warning" elevation={4}>
                  <Trans i18nKey="screens.cs.carrier_quotes.quote_updated" />
                </Alert>
              )}

              <Paper>
                <Stack sx={stackStyle}>
                  <Stack direction="row" marginBottom={2} justifyContent="space-between">
                    <Typography variant="h6" color="primary.main">
                      {t('screens.cs.carrier_quotes.transport_details')}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Typography variant="p4" color="primary.light">
                        {t('screens.cs.carrier_quotes.status')}
                      </Typography>
                      <Typography variant="p4" color="primary.main">
                        {isExpired ? 'EXPIRED' : quoteRequestQuery.data.status}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Grid container marginBottom={2} spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="p4" color="primary.main">
                        {t('screens.cs.carrier_quotes.kn_rep.kuehne_nagel_representative')}
                      </Typography>
                      <KNQuoteAttribute
                        value={quoteRequestQuery.data.contactPerson.name ?? '-'}
                        prefix={t('screens.cs.carrier_quotes.kn_rep.contact_name')}
                      />
                      <KNQuoteAttribute
                        value={quoteRequestQuery.data.contactPerson.email ?? '-'}
                        prefix={t('screens.cs.carrier_quotes.kn_rep.contact_email')}
                      />
                      <KNQuoteAttribute
                        value={quoteRequestQuery.data.tenderReference ?? '-'}
                        prefix={t('screens.cs.carrier_quotes.quote_reference')}
                      />
                      {quoteRequestQuery.data.expirationDate && (
                        <KNQuoteAttribute
                          value={localDate(quoteRequestQuery.data.expirationDate, 'short_with_time') ?? '-'}
                          prefix={isExpired ? t('screens.cs.carrier_quotes.quote_expired') : t('screens.cs.carrier_quotes.quote_expiration')}
                        />
                      )}
                    </Grid>
                    {(hasTemperatureRequirements || hasTruckRequirements || hasTrailerRequirements) && (
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="p4" color="primary.main">
                          {t('screens.cs.carrier_quotes.equipment.equipment_requirements')}
                        </Typography>
                        {hasTruckRequirements && (
                          <KNQuoteAttribute
                            value={quoteRequestQuery.data.requirements?.truck?.details ?? '-'}
                            prefix={t('screens.cs.carrier_quotes.equipment.truck')}
                            icon={<KNTruckIcon />}
                          />
                        )}
                        {hasTrailerRequirements && (
                          <KNQuoteAttribute
                            value={quoteRequestQuery.data.requirements?.trailer?.details ?? '-'}
                            prefix={t('screens.cs.carrier_quotes.equipment.trailer')}
                            icon={<KNTrailerIcon />}
                          />
                        )}
                        {hasTemperatureRequirements && (
                          <KNQuoteAttribute
                            value={t('screens.cs.carrier_quotes.equipment.range', {
                              rangeMin: getTemperatureMeasurement(quoteRequestQuery.data.requirements?.temperature?.minimum!, guestUserState.measurementsSystem), // NOSONAR
                              rangeMax: getTemperatureMeasurement(quoteRequestQuery.data.requirements?.temperature?.maximum!, guestUserState.measurementsSystem), // NOSONAR
                              unit: getFormattedUnit(getTemperatureUnit(guestUserState.measurementsSystem)),
                            })}
                            prefix={t('screens.cs.carrier_quotes.equipment.temperature')}
                            icon={<KNTemperatureTrackingIcon />}
                          />
                        )}
                      </Grid>
                    )}

                    {quoteRequestQuery.data.instructions && <Grid size={{ sm: 12, md: 4 }}>
                      <Typography variant="p4" color="primary.main">
                        {t('screens.cs.carrier_quotes.special_instructions.special_instructions')}
                      </Typography>
                      <Typography component="p" variant="p3" color="primary.light">
                        {quoteRequestQuery.data.instructions}
                      </Typography>
                    </Grid>}
                  </Grid>
                  <Stack spacing={2} marginBottom={2}>
                    {quoteRequestQuery.data.shipments?.map((shipment, index) => (
                      <QuoteShipmentAccordion key={`${shipment.cargoItems[index]?.description}-${index}`} shipment={shipment} />
                    ))}
                  </Stack>
                  <KNCaption icon={<InfoIcon />}>{t('screens.cs.trip_details.local_time')}</KNCaption>
                </Stack>
              </Paper>

              {quoteAnswerQuery.isPending && (
                <Typography>{t('screens.cs.carrier_quotes.loading')}</Typography>
              )}
              <Paper>
                <Stack sx={stackStyle}>
                  {(isQuoteAnswerNotFoundError(quoteAnswerQuery.error) || isEditable) && (
                    <CarrierQuoteForm
                      onAction={handleQuoteAnswer}
                      quoteRequest={quoteRequestQuery.data}
                      quoteAnswer={quoteAnswerQuery.data}
                    />
                  )}
                  {quoteAnswerQuery.isSuccess && !isEditable && (
                    <CarrierQuoteAnswerSummary
                      quoteAnswer={quoteAnswerQuery.data}
                      quoteReference={quoteRequestQuery.data.tenderReference}
                    />
                  )}
                </Stack>
              </Paper>

              <QuoteAlert quoteStatus={quoteStatus} quoteSnackbarOpen={quoteAlert} handleSnackbarClose={handleSnackbarClose} />
            </Stack>
        )}
        <Footer />
      </Stack>
    </Container>
  )
}

export default QuoteLink
